/**
 * The board's key, in three parts that are always on the page:
 *
 *   BoardLegend   a DEMONSTRATIVE Living Legend for the board's statuses: each
 *                 entry shows a live sample drawn by the same component the
 *                 rows use (StatusBar for a Monday word, StateMark for an
 *                 eMerge state), the stable label inside it, the non-colour
 *                 marker, one plain line saying what it means, and its count
 *                 and share of the view with a small share bar (not a chart).
 *                 Given `onSelect`, each entry is a control: it switches to
 *                 that status's tab (aria-pressed); pressing it again or
 *                 "Show all" restores the whole view. Explanations are text on
 *                 the page, never hover-only; the viewer may fold them away,
 *                 and that choice is remembered (storage is optional).
 *   TabsBasis     one sentence saying what the tabs ARE right now (Monday
 *                 groups as of a mirror run, or Status words, and why), with
 *                 the failed-sync caveat when the mirror's last run failed.
 *   ColumnKey     every column's meaning and source, as a disclosure list, so
 *                 nothing a header says lives only in a hover title.
 */
import React, { useId, useState } from 'react';
import { Marker } from '../tide';
import type { LegendItem, MarkerShape } from '../tide';
import type { BoardColumn, BoardPage, BoardTabDef } from './types';
import { boardStamp, tabSourceText } from './format';
import { GroupMark, StatusBar, StateMark } from './cells';
import { memorySlug, readMemory, writeMemory } from './memory';

/** The tabs a key explains: not the main table, not an empty catch-all, and only coloured or Monday-sourced ones. */
function keyedTabs(tabs: BoardTabDef[], allKey = 'all'): BoardTabDef[] {
  return tabs
    .filter((t) => t.key !== allKey && (t.color || t.source !== 'EMERGE'))
    .filter((t) => !(t.hide_when_empty && (t.count ?? 0) === 0));
}

/** The non-colour marker a tab carries in the key: its own shape, else a square (a Monday word), a diamond (an eMerge lens) or a hatch (colour not held). */
export function tabMarker(t: Pick<BoardTabDef, 'marker' | 'color' | 'source'>): MarkerShape {
  return t.marker || (t.color ? (t.source === 'EMERGE' ? 'diamond' : 'square') : 'hatch');
}

/** `explain`: describe each item by its tab's `what` (a definition) instead of its source. */
export function legendItems(tabs: BoardTabDef[], { explain = false, units = ['row', 'rows'] }: { explain?: boolean; units?: [string, string] } = {}): LegendItem[] {
  return keyedTabs(tabs).map((t) => ({
    key: t.key,
    label: t.label,
    color: t.color || 'var(--tide-status-missing)',
    marker: tabMarker(t),
    // A status word's source is said once, in the scope line; an item only explains what differs (a lens, a group's run, a missing colour).
    description: explain ? t.what : [t.source === 'STATUS' ? null : tabSourceText(t), t.color ? null : 'Monday colour not recorded'].filter(Boolean).join(' · ') || undefined,
    value: typeof t.count === 'number' ? t.count : null,
    unit: t.count === 1 ? units[0] : units[1],
  }));
}

/** The catch-all of rows the Monday mirror has not placed in a group (an eMerge catch-all, not a Monday group). */
export function isNoGroupTab(t: Pick<BoardTabDef, 'key' | 'source' | 'empty'>): boolean {
  return t.source === 'GROUP' && (t.empty === true || t.key === 'g-none');
}

/** The tab of rows with no status word. */
export function isEmptyStatusTab(t: Pick<BoardTabDef, 'key' | 'source' | 'empty'>): boolean {
  return t.source === 'STATUS' && (t.empty === true || t.key === 'no-status');
}

/**
 * The sample a row would draw for this tab: the same component the cells use.
 *   a marker tab (an eMerge state)  StateMark, as the state column;
 *   a Monday group                  GroupMark, as the group column (a group is
 *                                   not a status: it is never drawn as a chip);
 *   no status word                  the empty StatusBar a row with no word draws;
 *   a status word                   StatusBar, as the Status column.
 */
export function defaultLegendSample(t: BoardTabDef): React.ReactNode {
  if (t.marker) return <StateMark word={t.label} color={t.color} marker={t.marker} stateKey={t.key} />;
  if (t.source === 'GROUP') return <GroupMark title={isNoGroupTab(t) ? null : t.label} />;
  if (isEmptyStatusTab(t)) return <StatusBar word={null} color={null} />;
  return <StatusBar word={t.label} color={t.color} />;
}

export interface BoardLegendProps {
  title: string;
  tabs: BoardTabDef[];
  scope?: string;
  /** Kept for callers of the earlier static key; every entry now says what it means. */
  explain?: boolean;
  units?: [string, string];
  /** The tab in force. With `onSelect`, entries are controls. */
  value?: string;
  onSelect?: (key: string) => void;
  /** The tab that means "everything", used by Show all. */
  allKey?: string;
  /** Draw a tab's sample exactly as the rows draw it (default: StateMark for a marker tab, StatusBar otherwise). */
  sample?: (tab: BoardTabDef) => React.ReactNode;
}

const fmt = (n: number) => n.toLocaleString('en-US');

/** A definition's first sentence, and the rest. */
export function firstSentence(text: string): [string, string] {
  const m = text.match(/^(.+?[.!?])\s+(\S[\s\S]*)$/);
  return m ? [m[1], m[2]] : [text, ''];
}

export default function BoardLegend({
  title, tabs, scope, units = ['row', 'rows'], value, onSelect, allKey = 'all', sample = defaultLegendSample,
}: BoardLegendProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const store = `legend.${memorySlug(title)}`;
  const [folded, setFolded] = useState<boolean>(() => readMemory<boolean>(store, false) === true);
  const entries = keyedTabs(tabs, allKey);
  if (!entries.length) return null;

  const allTab = tabs.find((t) => t.key === allKey) || null;
  const counted = entries.every((t) => typeof t.count === 'number');
  const summed = entries.reduce((a, t) => a + (t.count || 0), 0);
  // The whole a share is of: the main table's count when the key has it, else the entries' own sum.
  const whole = allTab && typeof allTab.count === 'number' ? allTab.count : (counted ? summed : null);
  const wholeLine = whole === null ? null : (allTab ? `${fmt(whole)} ${units[whole === 1 ? 0 : 1]} in ${allTab.label}` : `${fmt(whole)} ${units[whole === 1 ? 0 : 1]} across these ${entries.length}`);
  const ofWhole = allTab ? allTab.label : `all ${whole === null ? '' : fmt(whole)}`.trim();
  const unit = (n: number) => (n === 1 ? units[0] : units[1]);
  // Monday's colours are Monday reference: said once for the key when every colour is Monday's, else on each Monday entry.
  const coloured = entries.filter((t) => t.color);
  const allMonday = coloured.length > 0 && coloured.every((t) => t.source !== 'EMERGE');
  const select = onSelect;
  const pressedTab = entries.find((t) => t.key === value) || null;
  const showingAll = value === allKey;
  const titleId = `${uid}-title`;
  const listId = `${uid}-list`;
  const fold = () => { const next = !folded; setFolded(next); writeMemory(store, next); };

  return (
    <div className="board-legend" role="group" aria-labelledby={titleId} data-folded={folded ? 'true' : 'false'} data-interactive={select ? 'true' : 'false'}>
      <div className="board-legend__head">
        <p id={titleId} className="board-legend__title">{title}</p>
        {wholeLine && <p className="board-legend__whole">{wholeLine}</p>}
        {allMonday && <p className="board-legend__tag board-legend__tag--head">Colours: Monday reference</p>}
        {select && (
          <p className="board-legend__status" aria-live="polite">
            {showingAll
              ? `Showing all${whole !== null ? ` ${fmt(whole)} ${unit(whole)}` : ''}${allTab ? ` in ${allTab.label}` : ''}.`
              : pressedTab
                ? `Showing only ${pressedTab.label}${typeof pressedTab.count === 'number' ? `: ${fmt(pressedTab.count)} ${unit(pressedTab.count)}` : ''}.`
                : 'Showing a view this key does not colour.'}
          </p>
        )}
        <span className="board-legend__actions">
          {select && (
            <button type="button" className="board-legend__all" aria-pressed={showingAll} onClick={() => select(allKey)}>
              {showingAll ? 'Showing all' : `Show all${allTab ? ` (${allTab.label})` : ''}`}
            </button>
          )}
          <button type="button" className="board-legend__fold" aria-expanded={!folded} aria-controls={listId} onClick={fold}>
            {folded ? 'Show what each means' : 'Hide explanations'}
          </button>
        </span>
      </div>
      {scope && !folded && <p className="board-legend__scope">{scope}</p>}
      <ul className="board-legend__list" id={listId}>
        {entries.map((t) => {
          const n = typeof t.count === 'number' ? t.count : null;
          const pct = n !== null && whole ? Math.round((n / whole) * 100) : null;
          const share = n !== null && whole ? Math.min(100, (n / whole) * 100) : 0;
          const descId = `${uid}-${t.key}-desc`;
          const pressed = value === t.key;
          const monday = t.source !== 'EMERGE';
          const [lead, rest] = firstSentence(t.what || '');
          const body = (
            <>
              {/* The marker and the sample stay together on one line: a marker never sits alone above its sample. */}
              <span className="board-legend__key">
                {!t.marker && <span className="board-legend__marker"><Marker shape={tabMarker(t)} color={t.color || 'var(--tide-status-missing)'} size={12} /></span>}
                <span className="board-legend__sample">{sample(t)}</span>
              </span>
              {t.source === 'GROUP' && !isNoGroupTab(t) && <span className="board-legend__kind">Monday group</span>}
              <span className="board-legend__nums">
                <span className="board-legend__count">{n === null ? 'not counted' : `${fmt(n)} ${unit(n)}`}</span>
                <span className="board-legend__pct">
                  {pct === null ? '' : `${pct}%`}
                  {pct !== null && <span className="board-sr"> of {ofWhole}</span>}
                </span>
              </span>
              <span className="board-legend__share" aria-hidden="true">
                <span className="board-legend__sharebar" style={{ width: `${share}%`, background: t.color || 'var(--tide-status-missing)' }} />
              </span>
            </>
          );
          return (
            <li key={t.key} className="board-legend__item" data-key={t.key} data-pressed={pressed ? 'true' : 'false'}>
              {select ? (
                <button
                  type="button"
                  className="board-legend__entry"
                  aria-pressed={pressed}
                  aria-describedby={descId}
                  onClick={() => select(pressed ? allKey : t.key)}
                >
                  {body}
                </button>
              ) : (
                <div className="board-legend__entry">{body}</div>
              )}
              <p id={descId} className="board-legend__desc" hidden={folded}>
                {/* One plain line: the definition's first sentence. The whole definition is the tab's own line above the table once chosen, and is read out here in full. */}
                <span>{lead}</span>
                {rest && <span className="board-sr"> {rest}</span>}
                {monday && t.color && !allMonday && <span className="board-legend__tag">Monday reference colour</span>}
                {monday && !t.color && <span className="board-legend__tag board-legend__tag--none">Monday colour not recorded</span>}
                {t.source === 'GROUP' && <span className="board-legend__tag">{t.source_text || (boardStamp(t.as_of) ? `read as of ${boardStamp(t.as_of)}` : 'read time not recorded')}</span>}
              </p>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const REASON: Record<string, string> = {
  NO_POOL: 'eMerge has no read connection to the Monday mirror on this server.',
  NO_COMPLETED_RUN: 'the Monday mirror has not completed a read of this board yet.',
  NO_GROUPS: 'the Monday mirror does not carry this board\'s groups yet.',
  UNAVAILABLE: 'the Monday mirror could not be read just now.',
};

export function TabsBasis({ page }: { page: Pick<BoardPage<unknown>, 'mode' | 'groups'> }) {
  if (page.mode === 'GROUP') {
    const at = boardStamp(page.groups.as_of) || 'an unrecorded time';
    return (
      <>
        <p className="board-basis">Tabs are the board&rsquo;s Monday groups, read from the Monday mirror as of {at}. Status stays a column.</p>
        {page.groups.sync_failed && (
          <p className="board-caveat" role="note">Last Monday sync of this board failed; groups as of {at}.</p>
        )}
      </>
    );
  }
  return (
    <p className="board-basis">
      Tabs are the board&rsquo;s Status words. Monday groups replace them here once the Monday mirror carries them
      {REASON[page.groups.state] ? <> — for now, {REASON[page.groups.state]}</> : '.'}
    </p>
  );
}

export function ColumnKey<T>({ columns, identity, notes = [] }: { columns: Array<Pick<BoardColumn<T>, 'key' | 'label' | 'help'>>; identity?: { label: string; help: string }; notes?: string[] }) {
  return (
    <details className="board-colkey">
      <summary>What each column means, and where it comes from</summary>
      <dl>
        {identity && (<><dt>{identity.label}</dt><dd>{identity.help}</dd></>)}
        {columns.map((c) => (<React.Fragment key={c.key}><dt>{c.label}</dt><dd>{c.help}</dd></React.Fragment>))}
      </dl>
      {notes.map((n) => <p key={n} className="board-colkey__note">{n}</p>)}
    </details>
  );
}
